import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, RefreshCw, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function EstadosValidoCreditoList() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { token } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

    const getDescripcionEstado = (valor) => {
        const descripciones = {
            'ACT': 'ACTIVO',
            'PEN': 'PENDIENTE',
            'INAC': 'INACTIVO',
            'INA': 'INACTIVO (OBSOLETO)', // Por si acaso hay remanentes de INA
            'SOL': 'SOLICITANTE',
            'REC': 'RECHAZADO',
            'PRS': 'PRE_SOCIO',
            'BLO': 'BLOQUEADO',
            'JUV': 'SOCIO JUVENIL'
        };
        return descripciones[valor] || 'OTRO / SIN DEFINIR';
    };

    const cargarDatos = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/parametrizacion/estados-valido-credito`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Error al cargar datos');
            const result = await response.json();
            setData(result);
            setError(null);
        } catch (error) {
            console.error('Error:', error);
            setError('No se pudieron cargar las reglas de validación.');
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarDatos();
    }, []);

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar esta regla?')) {
            try {
                const response = await fetch(`${API_URL}/parametrizacion/estados-valido-credito/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    toast.success('Regla eliminada exitosamente');
                    cargarDatos();
                } else {
                    throw new Error('Error al eliminar');
                }
            } catch (error) {
                toast.error('No se pudo eliminar la regla');
            }
        }
    };

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-enterprise">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-[#2d4a5d] p-3 flex justify-between items-center text-white rounded-t-sm">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion/productos')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-sm font-bold tracking-tight uppercase">Mantenimiento: Validación de Créditos por Estado</h1>
                </div>

                <Link
                    to="/parametrizacion/estados-valido-credito/nuevo"
                    className="flex items-center gap-1 transition-all rounded-sm text-[11px] font-bold px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white shadow-sm uppercase"
                >
                    <PlusCircle className="w-3.5 h-3.5" />
                    NUEVA REGLA
                </Link>
            </div>

            {/* Content Table Area */}
            <div className="overflow-x-auto relative">
                {error && (
                    <div className="m-4 p-4 bg-red-50 border border-red-200 text-red-700 flex items-center rounded-sm text-sm">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        {error}
                    </div>
                )}

                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-[#f8fafc] border-b border-gray-200 text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                            <th className="px-6 py-3 border-r border-gray-100 w-16 text-center">ID</th>
                            <th className="px-6 py-3 border-r border-gray-100 w-32">Código (Valor)</th>
                            <th className="px-6 py-3 border-r border-gray-100">Descripción del Estado</th>
                            <th className="px-6 py-3 border-r border-gray-100 text-center w-40">Válido para Crédito</th>
                            <th className="px-6 py-3 text-center w-48">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm text-gray-600">
                        {loading ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-12 text-center">
                                    <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
                                </td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-12 text-center text-gray-500 italic">
                                    No hay reglas configuradas.
                                </td>
                            </tr>
                        ) : (
                            data.map((item) => (
                                <tr key={item.id} className="border-b border-gray-100 hover:bg-slate-50 transition-colors font-enterprise">
                                    <td className="px-6 py-3 border-r border-gray-100 text-center text-gray-400 text-xs">
                                        {item.id}
                                    </td>
                                    <td className="px-6 py-3 border-r border-gray-100 font-bold text-gray-700 uppercase font-mono">
                                        {item.valor}
                                    </td>
                                    <td className="px-6 py-3 border-r border-gray-100 text-gray-600 font-medium">
                                        {getDescripcionEstado(item.valor)}
                                    </td>
                                    <td className="px-6 py-3 border-r border-gray-100 text-center">
                                        {item.credito === 'S' ? (
                                            <span className="px-3 py-1 bg-green-100 text-green-700 text-[10px] font-black border border-green-200 rounded-sm">SÍ</span>
                                        ) : (
                                            <span className="px-3 py-1 bg-red-100 text-red-700 text-[10px] font-black border border-red-200 rounded-sm">NO</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                        <div className="flex justify-center items-center gap-2">
                                            <Link
                                                to={`/parametrizacion/estados-valido-credito/${item.id}/editar`}
                                                className="text-blue-600 hover:text-blue-800 font-bold text-[11px] uppercase tracking-tighter"
                                            >
                                                EDITAR
                                            </Link>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="text-red-600 hover:text-red-800 font-bold text-[11px] uppercase tracking-tighter"
                                            >
                                                ELIMINAR
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer */}
            <div className="p-4 bg-slate-50 border-t border-gray-200 text-[11px] text-gray-400 italic">
                * El código de estado debe coincidir exactamente con el campo 'valor' de la tabla estado_socio (ej. PRS, ACT).
            </div>
        </div>
    );
}
